import { NgTemplateOutlet } from '@angular/common'
import { Component, inject, Input, LOCALE_ID, OnDestroy, OnInit, ViewChild } from '@angular/core'

import { FormsModule } from '@angular/forms'
import { ActivatedRoute, NavigationStart, Router } from '@angular/router'
import { Subscription } from 'rxjs'

import { PrimeTemplate } from 'primeng/api'
import { BadgeDirective, Badge } from 'primeng/badge'
import { Button } from 'primeng/button'
import { DatePicker } from 'primeng/datepicker'
import { InputNumber } from 'primeng/inputnumber'
import { InputText } from 'primeng/inputtext'
import { MultiSelect } from 'primeng/multiselect'
import { Popover } from 'primeng/popover'
import { Select } from 'primeng/select'
import { cloneDeep, isEmpty, isEqual, isUndefined } from 'lodash-es'

import { GenericColumnComponent } from '@app/components/generic/generic-column'
import { buildFilterKeyValue, cleanMapForPrefix, paramMapToMap, urlParamsToLazyLoadEvent, urlSearchParamsToMap } from '@app/components/generic/utilities'
import { GlobalSearchService } from '@app/components/global-search/global-search.service'
import { StopClickPropagationDirective } from '@app/directives/stop-click-propagation.directive'

import { ConfigureFiltersService } from './configure-filters.service'
import { Filter, FilterServicePayload, newFilter, newFilterServicePayload } from './filter'
import { FilterType } from './filter.constants'

@Component({
  selector: 'app-configure-filters',
  templateUrl: 'configure-filters.html',
  styleUrl: 'configure-filters.css',
  imports: [
    Button,
    BadgeDirective,
    Badge,
    Popover,
    Select,
    NgTemplateOutlet,
    FormsModule,
    InputText,
    PrimeTemplate,
    MultiSelect,
    DatePicker,
    StopClickPropagationDirective,
    InputNumber,
  ],
})
export class ConfigureFiltersComponent implements OnInit, OnDestroy {
  public readonly configureFiltersService = inject(ConfigureFiltersService)
  public readonly globalSearchService = inject(GlobalSearchService)
  public readonly router = inject(Router)
  public readonly activatedRoute = inject(ActivatedRoute)
  public readonly locale = inject(LOCALE_ID)

  @Input({ required: true }) uniqueKey: string = ''
  @Input() hideGlobalSearch: boolean = false
  @Input() disabled: boolean = false
  @Input() iconOnly: boolean = false
  @Input() dismissable: boolean = true

  @ViewChild('menu', { static: true }) public overlayPanel!: Popover

  public filterPayload: FilterServicePayload = newFilterServicePayload()
  public allColumns: GenericColumnComponent[] = []
  public previousFilterMap: Map<string, any> = new Map<string, any>()
  public filterLabelField: string = 'label_en'

  private columnsSubscription?: Subscription
  private filtersSubscription?: Subscription
  private globalSearchSubscription?: Subscription
  private routerSubscription?: Subscription

  private globalFilter?: Filter

  protected readonly FilterType = FilterType

  constructor () {
    if (this.locale === 'fr') {
      this.filterLabelField = 'label_fr'
    }
  }

  ngOnInit () {
    // Subscribe to the service that will return the total list of columns we should display as filterable
    this.columnsSubscription = this.configureFiltersService.getColumns().subscribe(value => {
      if (!this.uniqueKey) return
      this.allColumns = value.get(this.uniqueKey) ?? []
      this.allColumns = this.allColumns.filter(column => column.filterType !== 'none')

      // if this subscription call was for us, and we're not hiding the global search field, then add it now
      if (!this.hideGlobalSearch && this.allColumns.length > 0) this.allColumns.push(this.configureFiltersService.globalColumnComponent)
      this.configureFiltersService.attachColumns(this.uniqueKey)
    })

    // Subscribe to the service that will inform us when the table filters have changed, either from the URL
    // or from the table column filters… and hence to update the filters in the widget
    this.filtersSubscription = this.configureFiltersService.getFilters().subscribe(filterPayloads => {
      if (!this.uniqueKey) return

      // Reset the total list of filters to be those from the table and the global filter
      setTimeout(() => {
        const payload = filterPayloads.get(this.uniqueKey)
        if (!payload) return
        this.filterPayload = payload
        this.filterPayload.filters = this.combineFiltersAndGlobal(payload.filters, this.globalFilter)
        // Update our idea of the 'previous' filters, to enable us to detect when no change was made to the filters when
        // the dialog is shown then hidden
        this.previousFilterMap = this.getFilterMap(this.filterPayload.filters)
      })
    })

    // Subscribe to the service that will inform us when a global search is performed, which we will combine with the normal
    // table filters to build the total list of filters for the widget
    this.globalSearchSubscription = this.globalSearchService.getValue().subscribe(valueMap => {
      this.globalFilter = undefined
      valueMap.forEach((value, key) => {
        if (key === this.uniqueKey) {
          if (!isEmpty(value)) {
            this.globalFilter = {
              value: value,
              matchMode: 'equals',
              operator: 'and',

              columnKey: 'global_search',
              filterKey: 'global_search',
              columnComponent: this.configureFiltersService.globalColumnComponent,
              filterMatchModes: this.configureFiltersService.getFilterMatchModes(FilterType.GLOBAL),
            }
          }
        }
      })

      // Reset the total list of filters to be those from the table and the global filter
      this.filterPayload.filters = this.combineFiltersAndGlobal(this.filterPayload.filters, this.globalFilter)
      // Update our 'previous' value in order to detect when the widget is used but makes no changes to the filters
      // to avoid unnecessary reloads
      this.previousFilterMap = this.getFilterMap(this.filterPayload.filters)
    })

    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        // detect that the navigation event is the back / forward button
        if (event.navigationTrigger === 'popstate') {
          // need to determine if we are within the same angular route, because refreshing the sort / filter manually like this
          // is only necessary when we are staying in the same component / route
          const newUrlRoot = event.url.split('?')[0]
          const oldUrlRoot = this.router.url.split('?')[0]

          if (newUrlRoot === oldUrlRoot) {
            // we're in the same route, convert the url params to a map
            const searchParams: URLSearchParams = new URLSearchParams(event.url.split('?')[1])
            let map = urlSearchParamsToMap(searchParams)
            map = cleanMapForPrefix(map, this.uniqueKey)
            // update the globalSearchService from the new url
            this.globalSearchService.setValue({ uniqueKey: this.uniqueKey, value: map.get('global_search') || '' })

            const lazy = urlParamsToLazyLoadEvent(map)
            const payload = this.configureFiltersService.lazyLoadEventToFilterServicePayload(lazy, this.uniqueKey)
            // Use `cloneDeep` instead of `structuredClone` because filters[].columnComponent is a live GenericColumnComponent instance.
            this.configureFiltersService.setFiltersChanged({ uniqueKey: this.uniqueKey, payload: cloneDeep(payload) })
          }
        }
      }
    })

    let map = paramMapToMap(this.activatedRoute.snapshot?.queryParamMap)
    map = cleanMapForPrefix(map, this.uniqueKey)
    if (map.get('global_search')) {
      this.globalSearchService.setValue({ uniqueKey: this.uniqueKey, value: map.get('global_search') })
    }
    const lazy = urlParamsToLazyLoadEvent(map)
    this.configureFiltersService.lazyLoadEventToFilterServicePayload(lazy, this.uniqueKey)
  }

  /**
   * Return a list of filters combining the incoming table filters (which potentially include a global filter, and a new global filter
   */
  combineFiltersAndGlobal (filters: Filter[], globalFilter?: Filter): Filter[] {
    // remove the old global filter from the list, if it exists
    const combinedFilters = filters.filter(filter => filter.filterKey !== this.configureFiltersService.globalColumnComponent.key)
    if (!this.hideGlobalSearch && globalFilter) {
      combinedFilters.push(globalFilter)
    }
    return combinedFilters
  }

  ngOnDestroy () {
    this.columnsSubscription?.unsubscribe()
    this.filtersSubscription?.unsubscribe()
    this.globalSearchSubscription?.unsubscribe()
    this.routerSubscription?.unsubscribe()
  }

  /**
   * overlay is dismissed, remove empty filters and call change event if the resulting filters were changed
   */
  onOverlayHide () {
    this.applyFilters()
  }

  /**
   * convert the list of filters to a map of key/values for accurate comparison
   */
  getFilterMap (filters: Filter[]): Map<string, any> {
    const map = new Map<string, any>()
    filters.forEach(filter => {
      if (!filter.columnComponent) return
      const result = buildFilterKeyValue(filter, filter.filterKey!)
      map.set(result[0], result[1])
    })
    return map
  }

  /**
   * the user changed the "field", need to rebuild the filter object including resetting the potential matchModes
   */
  onChangeFilterField (filter: Filter) {
    // reset the current matchMode
    filter.matchMode = undefined
    // reset the current value to empty
    filter.value = undefined

    filter.columnComponent = this.allColumns.find((column: GenericColumnComponent) => {
      return (filter.columnKey === column.key)
    })
    filter.columnKey = filter.columnComponent?.key
    filter.filterKey = filter.columnComponent?.filterKey || filter.columnComponent?.key
    filter.filterMatchModes = this.configureFiltersService.getFilterMatchModes(filter.columnComponent?.filterType)
    // choose the first match mode automatically
    filter.matchMode = filter.filterMatchModes[0].value?.toString()
  }

  deleteFilter (index: number) {
    this.filterPayload.filters.splice(index, 1)
  }

  addFilter () {
    this.filterPayload.filters.push(newFilter())
  }

  applyFilters () {
    // remove any incomplete filters
    this.filterPayload.filters = this.filterPayload.filters.filter(filter => {
      // 'exists' matchModes don't need a value coz it's implied by the matchMode
      if (['exists', 'notExists'].includes(filter.matchMode!)) filter.value = true
      // otherwise only allow filters with defined values
      return !isUndefined(filter.value) && filter.value !== '' && !isEqual(filter.value, [''])
    })
    const filterMap = this.getFilterMap(this.filterPayload.filters)

    // if no change was made to filters then don't call the change event
    if (isEqual(filterMap, this.previousFilterMap)) return

    // update the global search using the value in our widget
    if (!this.hideGlobalSearch) this.globalSearchService.setValue({ uniqueKey: this.uniqueKey, value: filterMap.get('global_search') || '' })

    // Calling the update of filters on the service needs to be performed in a setTimeout() to ensure
    // the new globalSearch value will be taken into account when the table refreshes.. :|
    setTimeout(() => {
      // the user changed the filters, call the change event and update the 'current' filters for future comparison
      this.configureFiltersService.setFiltersChanged({ uniqueKey: this.uniqueKey, payload: this.filterPayload })
    })

    this.previousFilterMap = filterMap
  }

  clearFilters () {
    this.filterPayload.filters.splice(0, this.filterPayload.filters.length)
    this.applyFilters()
  }
}
